/* Asha v2 — shared site data.
   No pricing, no testimonials, no customer names, no ROI/performance claims. */

/* ============ AI WORKFORCE ============ */

export const AGENTS_FRONT = [
  {
    name: 'Amy',
    role: 'AI Receptionist',
    img: '/assets/amy.png',
    tag: '01',
    desc: 'Answers every call 24/7, in any language, and books straight into the schedule.',
    detail:
      'Amy handles the calls your front desk can\'t get to — after hours, during lunch, when all three lines ring at once. She triages emergencies, checks live insurance mid-call, quotes what the visit will look like, and books the patient into the right provider, operatory, and time block on the first call.',
    points: [
      'Answers 24/7 — nights, weekends, lunch rushes, and overflow',
      'Books directly into the Asha schedule with provider and operatory rules applied',
      'Triages emergencies in real time — swelling, fever, trauma',
      'Verifies insurance live during the call, before the patient hangs up',
      'Handles the hard calls: anxious patients, cost objections, language barriers',
      'Every call recorded and transcribed into the patient timeline',
    ],
  },
  {
    name: 'Leena',
    role: 'Schedule Optimizer',
    img: '/assets/leena.png',
    tag: '02',
    desc: 'Hunts gaps in the schedule and fills them with the right patient, provider, and operatory.',
    detail:
      'When a cancellation lands, Leena is already working it. She reads clinical need, insurance status, provider capability, and operatory equipment — then runs multi-channel outreach until the chair is full again. Not a waitlist blast; a reasoned match.',
    points: [
      'Fills same-day cancellations with production-aware prioritization',
      'Groups similar procedures so providers stay in flow',
      'Respects time-block rules, credentials, and operatory equipment',
      'Texts, calls, and leaves voicemail — matched to each patient\'s preference',
      'Negotiates swaps when patients push back on timing',
      'Sends prep instructions and tracks no-show risk on every fill',
    ],
  },
  {
    name: 'Stephanie',
    role: 'Hygiene Recall',
    img: '/assets/stephanie.png',
    tag: '03',
    desc: 'Calls overdue hygiene patients with full chart context — conversations, not robocalls.',
    detail:
      'Stephanie remembers every prior conversation — what was said, why the patient delayed, how they felt about it. She reads chart notes and personalizes the clinical reason to come back, then books into the right hygienist and operatory.',
    points: [
      'Voice-first outreach that patients actually pick up and finish',
      'Reads the chart: frames recall around the patient\'s actual diagnosis',
      'Remembers past objections and resolves root causes, not symptoms',
      'Snoozes intelligently — traveling patients resume on return',
      'Falls back to SMS and respects requested call-back windows',
    ],
  },
  {
    name: 'Kim',
    role: 'Case Acceptance',
    img: '/assets/kim.png',
    tag: '04',
    desc: 'Follows up on every unscheduled treatment plan until the patient says yes or no.',
    detail:
      'Unscheduled treatment is the quietest leak in dentistry. Kim scans for unaccepted plans, prioritizes by urgency, educates the patient on why the treatment matters, and works through financial objections with phasing and payment options.',
    points: [
      'Finds every unscheduled treatment plan in the PMS automatically',
      'Prioritizes by clinical urgency, not just dollar value',
      'Explains treatment in plain language patients understand',
      'Phases care when budget is the blocker — urgent first, electives later',
      'Multi-channel: calls, texts, and education sent to the patient portal',
    ],
  },
  {
    name: 'Elise',
    role: 'Patient Experience',
    img: '/assets/elise.png',
    tag: '05',
    desc: 'Runs post-visit workflows and turns happy visits into public reviews.',
    detail:
      'Elise checks in with every patient after the visit. Happy patients get a one-tap review link. Unhappy patients get a real conversation — she investigates what went wrong, routes it to the team, and follows up until it\'s resolved.',
    points: [
      'Post-visit voice and SMS check-ins for every appointment',
      'Routes happy patients to public reviews at the moment of delight',
      'Catches negative experiences within minutes, not weeks',
      'Investigates root causes and flags them for the team',
      'Turns detractors into advocates by actually fixing the issue',
    ],
  },
  {
    name: 'Morgan',
    role: 'Patient Intake',
    img: '/assets/morgan.png',
    tag: '06',
    desc: 'Collects forms, history, and insurance before the patient reaches the chair.',
    detail:
      'Morgan chases down intake the way a great front desk would — if it had infinite patience. Forms by text, insurance cards by photo, medical history validated, and clinical alerts briefed to the provider before the patient sits down.',
    points: [
      'Chases digital forms by SMS and voice until they\'re done',
      'Patients text a photo of their card — data extracted instantly',
      'Flags medical alerts: allergies, devices, drug interactions',
      'Validates guardianship, POA, and consent for complex cases',
      'Hands off to insurance verification the moment intake completes',
    ],
  },
  {
    name: 'Trisha',
    role: 'AI Practice Manager',
    img: '/assets/trisha.png',
    tag: '07',
    desc: 'Keeps an operations dashboard of every task the AI team is running.',
    detail:
      'Trisha is the manager of the AI team itself. She runs morning diagnostics across every agent, detects when a workflow is blocked, initiates workarounds, and escalates the things that genuinely need a human decision.',
    points: [
      'Morning health check across the entire AI workforce',
      'Detects blocked workflows and starts workarounds automatically',
      'Escalates compliance, legal, and financial risks to humans first',
      'One dashboard: everything the AI team did, is doing, and needs',
    ],
  },
  {
    name: 'Joseph',
    role: 'Fax Manager',
    img: '/assets/joseph.png',
    tag: '08',
    desc: 'Reads, routes, and files every inbound fax to the right patient record.',
    detail:
      'Referrals, lab work, EOBs — they still arrive by fax. Joseph reads every page, identifies the patient and document type, files it to the chart, and alerts whoever needs to act on it.',
    points: [
      'Parses every inbound fax: referrals, labs, EOBs, records requests',
      'Files documents to the correct patient automatically',
      'Flags errors and unreadable pages before they cause problems',
      'Watches for expected documents and confirms their arrival',
    ],
  },
];

export const AGENTS_REVENUE = [
  {
    name: 'Amanda',
    role: 'Insurance Verification',
    img: '/assets/amanda.png',
    tag: '01',
    desc: 'Verifies eligibility and builds procedure-level benefit breakdowns days ahead.',
    detail:
      'Amanda verifies every scheduled patient before they arrive — not just "active or inactive," but procedure-level coverage, frequencies, deductibles, and remaining maximums, saved straight into the chart. When something looks like a future denial, she flags it days in advance.',
    points: [
      'Continuous verification for every patient on the schedule',
      'Procedure-level breakdowns: coverage, frequency limits, deductibles',
      'Escalation flags on procedures likely to be denied',
      'Real-time verification during live calls when patients ask',
      'Full benefit breakdown saved to the PMS, not a PDF in a folder',
    ],
  },
  {
    name: 'Alayna',
    role: 'Claim Scrubbing',
    img: '/assets/alayna.png',
    tag: '02',
    desc: 'Scrubs every claim against payer rules before it ever leaves the building.',
    detail:
      'Most denials are preventable administrative errors. Alayna checks every claim against payer-specific rules — bundling, frequencies, modifiers, missing surfaces, missing diagnostics — before submission, so the claim is clean the first time.',
    points: [
      'Payer-specific rule engine: bundling, frequency, modifiers',
      'Catches missing surfaces, codes, and pre-auth requirements',
      'Attaches the right diagnostics before the payer asks',
      'Learns each payer\'s quirks and applies them automatically',
    ],
  },
  {
    name: 'Ariel',
    role: 'Claim Submission',
    img: '/assets/ariel.png',
    tag: '03',
    desc: 'Submits clean claims into the clearinghouse the moment treatment completes.',
    detail:
      'Ariel batches and submits claims with narratives, X-rays, and clinical justification attached. She tracks acknowledgments, watches timely-filing deadlines, and handles primary/secondary coordination without a human touching the queue.',
    points: [
      'Auto-generates claims from completed treatment',
      'Attaches narratives, radiographs, and clinical evidence',
      'Tracks clearinghouse acknowledgments end to end',
      'Coordinates multi-payer claims: primary, secondary, COB rules',
      'Watches timely-filing windows so nothing expires quietly',
    ],
  },
  {
    name: 'Sarah',
    role: 'Denial Management',
    img: '/assets/sarah.png',
    tag: '04',
    desc: 'Reasons across EOBs, notes, and payer policy to draft one-tap appeals.',
    detail:
      'When a claim is denied, Sarah loads the claim, EOB, clinical notes, radiographs, and payer policy in parallel — then reasons through whether to appeal, correct, or write off. If the clinical evidence supports an appeal, she drafts it with the evidence attached, ready for one-tap submission.',
    points: [
      'Multi-step reasoning across 16+ data points per denial',
      'Pulls clinical evidence: perio charting, radiographs, narratives',
      'Decides appeal vs. correct vs. write-off — and shows its work',
      'Generates appeal letters with documentation attached',
      'One-tap review and submission for your team',
    ],
  },
  {
    name: 'Alisha',
    role: 'EOB Posting',
    img: '/assets/alisha.png',
    tag: '05',
    desc: 'Posts payments and reconciles EOBs into the ledger without a human touch.',
    detail:
      'Electronic or paper, Alisha ingests every EOB, matches each service line against contracted rates, posts to the ledger, and reconciles deposits. When a payer pays under contract, she flags it — the underpayments most practices never notice.',
    points: [
      'Reads ERAs and scanned paper EOBs in one workflow',
      'Matches every service line against your contracted fees',
      'Flags underpayments that would otherwise slip through',
      'Reconciles bank deposits to EOBs to patient payments',
    ],
  },
  {
    name: 'Kianna',
    role: 'Patient Billing',
    img: '/assets/kianna.png',
    tag: '06',
    desc: 'Runs voice and text billing campaigns that patients actually respond to.',
    detail:
      'Kianna sends statements patients can understand — what insurance covered, what\'s left, and why. Then she follows up by voice and text, offers payment plans when money is tight, and handles hard conversations with actual sensitivity.',
    points: [
      'Statements written in plain language, not ledger codes',
      'Voice campaigns for balances that letters won\'t collect',
      'Payment plan and hardship options offered in the moment',
      'Text-to-pay links that close the loop in one tap',
    ],
  },
  {
    name: 'Julia',
    role: 'A/R & Collections',
    img: '/assets/julia.png',
    tag: '07',
    desc: 'Works the aging report automatically, oldest balances first.',
    detail:
      'Julia owns the aging report. She prioritizes by age and value, runs systematic follow-up across insurance and patient balances, and keeps receivables from quietly drifting past 90 days.',
    points: [
      'Continuous work-down of the A/R aging report',
      'Insurance follow-up and patient follow-up in one motion',
      'Prioritizes oldest and highest-value balances first',
      'Clean escalation paths when an account truly needs a human',
    ],
  },
  {
    name: 'Laura',
    role: 'Insurance Calling',
    img: '/assets/laura.png',
    tag: '08',
    desc: 'Sits on hold with payers so your team never has to.',
    detail:
      'When the portal is down or the answer needs a human at the payer, Laura calls. She navigates IVRs, waits on hold, asks the right questions, and documents rep names and reference numbers — every time.',
    points: [
      'Calls payer lines directly when portals can\'t answer',
      'Navigates phone trees and holds as long as it takes',
      'Documents rep name, reference number, and the full answer',
      'Feeds results straight back to verification and claims',
    ],
  },
];

export const CLINICAL_AI = [
  {
    name: 'AI Scribe',
    tag: '01',
    desc: 'Listens chairside and writes structured clinical notes with CDT codes in real time.',
    detail:
      'Speak naturally during the exam. The Scribe turns the conversation into structured, reviewable clinical notes with procedure codes attached — so charting ends when the appointment does.',
    points: [
      'Real-time transcription trained on dental terminology',
      'Structured notes with CDT codes, ready for provider review',
      'Multi-speaker aware: provider, assistant, patient',
      'Notes flow straight into the chart and the claim',
    ],
  },
  {
    name: 'AI Treatment Planner',
    tag: '02',
    desc: 'One click turns findings into a phased, insurance-aware plan with patient-friendly visuals.',
    detail:
      'The planner reads findings, history, and coverage — then generates a plan a patient can actually understand: what\'s happening, why it matters, what happens if you wait, and how the phases sequence with their benefits.',
    points: [
      'Generates a complete visual plan in seconds',
      'Plain-language clinical explanations, not code lists',
      'Phases treatment by urgency and insurance coverage',
      'Visual diagrams: pocket depths, tooth-by-tooth breakdowns',
    ],
  },
  {
    name: 'Voice Perio',
    tag: '03',
    desc: 'Hands-free perio charting — call out depths, watch the chart fill itself.',
    detail:
      'The hygienist calls out pocket depths, bleeding, and recession; the odontogram fills in real time. No re-entry, no second pass, no assistant tied up at the keyboard.',
    points: [
      'Voice-driven perio charting during the exam',
      'Captures depths, bleeding points, recession, mobility',
      'Live odontogram updates as you speak',
    ],
  },
  {
    name: 'AI Copilot',
    tag: '04',
    desc: 'An in-chart assistant that surfaces what matters about the patient in front of you.',
    detail:
      'The Copilot sits inside the chart with full context — history, findings, coverage, guidelines — and answers in plain language: what changed since last visit, what\'s outstanding, what to watch for.',
    points: [
      'Chairside answers with full patient context',
      'Surfaces history, alerts, and unfinished treatment',
      'Coverage-aware guidance before you recommend',
      'Best-practice prompts grounded in the patient record',
    ],
  },
];

/* ============ PLATFORM (10 SOFTWARE PRODUCTS) ============ */

export const PLATFORM_PRODUCTS = [
  {
    key: 'pms',
    index: '01',
    name: 'Practice Management',
    desc: 'Charting, perio, treatment plans, ledgers, scheduling — fully cloud, no servers.',
    replaces: 'Dentrix · Eaglesoft · Open Dental',
    features: [
      'Full 32-tooth odontogram with real-time condition tracking',
      'Perio charting: depths, bleeding, recession, mobility',
      'Treatment planning with phasing and presentation tracking',
      'Patient ledgers, billing, and audit trails',
      'Provider- and operatory-aware scheduling rules',
      'Cloud-native — no local server to maintain',
    ],
  },
  {
    key: 'comms',
    index: '02',
    name: 'Communication Suite',
    desc: 'VoIP phones, 2-way texting, team chat, intelligent fax, digital forms — one inbox.',
    replaces: 'Weave · Mango · RingCentral',
    features: [
      'Built-in VoIP with desk phones and softphones',
      'Unlimited 2-way patient texting in a universal inbox',
      'Team chat and channels, HIPAA compliant',
      'Intelligent fax: parsed, filed, and routed automatically',
      'Every call recorded and transcribed into the patient timeline',
    ],
  },
  {
    key: 'verify',
    index: '03',
    name: 'Insurance Verification',
    desc: 'Real-time, dual-source eligibility with full benefit breakdowns.',
    replaces: 'Standalone verification tools',
    features: [
      'Real-time eligibility checks before every appointment',
      'Procedure-level benefit breakdowns saved to the chart',
      'Dual-source verification when payers disagree with portals',
      'Escalation flags on likely denials, days in advance',
    ],
  },
  {
    key: 'claims',
    index: '04',
    name: 'eClaims Portal',
    desc: 'Built-in clearinghouse connectivity wired straight to the ledger.',
    replaces: 'Vyne · DentalXChange',
    features: [
      'Single-login claim submission, no third-party portal',
      'Attachments, narratives, and tracking in one place',
      'Acknowledgment monitoring and timely-filing alerts',
      'Wired directly to the ledger — no double entry',
    ],
  },
  {
    key: 'imaging',
    index: '05',
    name: 'Cloud Imaging',
    desc: 'DICOM-compatible imaging that works with the sensors you already own.',
    replaces: 'Local imaging servers',
    features: [
      'Native DICOM support for X-rays and intraoral photos',
      'Works with the sensors you already own',
      'Unlimited cloud storage, no on-site archive',
      'Side-by-side comparison across dates and mounts',
    ],
  },
  {
    key: 'payments',
    index: '06',
    name: 'Integrated Payments',
    desc: 'Text-to-pay, terminals, and automated payment reminders.',
    replaces: 'Standalone processors',
    features: [
      'Text-to-pay links that settle straight to the ledger',
      'In-office card terminals included',
      'Cards on file and configurable payment plans',
      'Automated, polite payment reminders',
    ],
  },
  {
    key: 'marketing',
    index: '07',
    name: 'Marketing Suite',
    desc: 'SMS and email campaigns with clinically-aware patient segmentation.',
    replaces: 'Mailchimp + patient marketing tools',
    features: [
      'SMS and email campaigns from one composer',
      'Clinically-aware segments: overdue hygiene, unscheduled treatment',
      'AI handles the replies, not just the sends',
      'Automated recall and reactivation workflows',
    ],
  },
  {
    key: 'analytics',
    index: '08',
    name: 'Revenue Intelligence',
    desc: 'KPI dashboards, daily huddles, production and collections insight.',
    replaces: 'Dental analytics add-ons',
    features: [
      'Live production and collections dashboards',
      'Daily huddle view: today\'s schedule, gaps, and alerts',
      'Provider and hygiene performance breakdowns',
      'Leak detection across the whole revenue cycle',
    ],
  },
  {
    key: 'portal',
    index: '09',
    name: 'Patient Portal',
    desc: 'Secure links for forms, treatment plans, and insurance uploads.',
    replaces: 'Forms + portal point tools',
    features: [
      'Secure, no-password patient links',
      'Interactive digital forms on any device',
      'Treatment plan viewing and acceptance',
      'Insurance card and document uploads',
    ],
  },
  {
    key: 'scheduling',
    index: '10',
    name: 'Online Scheduling',
    desc: 'Patients book real openings themselves — with ePrescribing alongside.',
    replaces: 'Online booking widgets',
    soon: true,
    features: [
      'Patients book real, rules-aware openings 24/7',
      'Reschedules and confirmations handled automatically',
      'ePrescribing arriving alongside',
    ],
  },
];

/* ============ COMMUNICATION SUITE DETAIL ============ */

export const COMMS_CHANNELS = [
  {
    key: 'voice',
    name: 'Voice & VoIP',
    desc: 'A phone system built into the OS — every call answered, recorded, transcribed, and filed to the patient.',
    points: [
      'Cloud VoIP with desk phones, softphones, and mobile',
      'Amy answers what your team can\'t get to, 24/7',
      'Recordings and AI transcripts on every patient timeline',
      'Smart routing: emergencies to on-call, routine to front desk',
    ],
  },
  {
    key: 'texting',
    name: '2-Way Texting',
    desc: 'One universal inbox for staff and AI agents — unlimited, contextual, and connected to the chart.',
    points: [
      'Unlimited 2-way SMS, no usage caps',
      'Full patient context next to every conversation',
      'AI agents and staff work the same inbox, visibly',
      'Confirmations, reminders, and follow-ups automated',
    ],
  },
  {
    key: 'fax',
    name: 'Intelligent Fax',
    desc: 'Faxes that read themselves — parsed, classified, and filed before anyone touches them.',
    points: [
      'Inbound referrals, labs, and EOBs parsed automatically',
      'Filed to the right patient record without manual sorting',
      'Outbound fax for records, slips, and treatment plans',
    ],
  },
  {
    key: 'forms',
    name: 'Next-Gen Forms',
    desc: 'Digital intake patients finish before they arrive — chased politely until done.',
    points: [
      'Mobile-first forms for intake, history, and consent',
      'Morgan chases completion by text and voice',
      'Insurance cards captured by photo, data extracted',
      'Answers flow into the chart, not a PDF',
    ],
  },
  {
    key: 'campaigns',
    name: 'Campaigns',
    desc: 'SMS, email, and voice campaigns segmented by clinical reality, not mailing lists.',
    points: [
      'Hygiene recall, reactivation, and education campaigns',
      'Clinically-aware segmentation from live chart data',
      'AI answers the replies and books the appointments',
    ],
  },
  {
    key: 'payments',
    name: 'Text-to-Pay & Terminals',
    desc: 'Payment built into the conversation — one tap from text to settled.',
    points: [
      'Text-to-pay links inside billing conversations',
      'In-office terminals on the same ledger',
      'Automated reminders that don\'t feel like collections',
    ],
  },
  {
    key: 'team',
    name: 'Team Chat',
    desc: 'Internal channels where your staff and your AI agents coordinate in the open.',
    points: [
      'HIPAA-compliant team messaging and channels',
      'AI agents post updates where the team can see them',
      'Chart context one click from any conversation',
    ],
  },
];

/* ============ ARIA ============ */

export const ARIA_LOOP = [
  {
    step: 'Reason',
    desc: 'ARIA reads the goal and the live state of the practice, then decides the next best action — which agent, which task, which order.',
  },
  {
    step: 'Act',
    desc: 'It dispatches a specialist: Amanda verifies, Laura calls the payer, Joseph watches the fax line, Ariel submits the claim. Real actions in real systems.',
  },
  {
    step: 'Observe',
    desc: 'ARIA reads what came back — the verification result, the payer\'s answer, the parsed fax — and judges whether the job is actually done.',
  },
  {
    step: 'Repeat',
    desc: 'If it isn\'t done, ARIA loops: new reasoning, next action, until the workflow is complete end to end. No hand-back to a human mid-task.',
  },
];

export const ARIA_EXAMPLE = [
  {
    round: '01',
    reasoning: '“Eight patients tomorrow. I\'ll verify insurance for all of them first.”',
    action: 'Dispatches Amanda → 7 of 8 verified instantly.',
    agent: 'Amanda',
  },
  {
    round: '02',
    reasoning: '“Mrs. Chen\'s coverage is 18 months stale and she\'s scheduled for a crown. I need live benefits.”',
    action: 'Dispatches Laura → calls the payer, gets confirmation on the line.',
    agent: 'Laura',
  },
  {
    round: '03',
    reasoning: '“The payer is faxing the breakdown. Someone should be watching for it.”',
    action: 'Dispatches Joseph → monitors the fax queue, parses the breakdown on arrival.',
    agent: 'Joseph',
  },
  {
    round: '04',
    reasoning: '“Coverage confirmed. Update the chart and brief the doctor.”',
    action: 'Updates Mrs. Chen\'s chart, sends the morning brief by SMS.',
    agent: 'ARIA',
  },
];

/* ============ THE PROBLEM / WHY ASHA ============ */

export const SPRAWL_STACK = [
  { name: 'Practice management', vendors: 'Dentrix · Eaglesoft · Open Dental' },
  { name: 'Phones & communication', vendors: 'Weave · Mango · RingCentral' },
  { name: 'Insurance verification', vendors: 'Zuub · standalone tools' },
  { name: 'Claims & clearinghouse', vendors: 'Vyne · DentalXChange' },
  { name: 'Payments', vendors: 'Square · standalone processors' },
  { name: 'Patient marketing', vendors: 'RevenueWell · Mailchimp' },
  { name: 'Imaging', vendors: 'Dexis · Carestream · local servers' },
  { name: 'Analytics', vendors: 'Dental Intel · spreadsheets' },
  { name: 'Forms & intake', vendors: 'Point tools · paper' },
  { name: 'AI receptionist', vendors: 'Single-agent point solutions' },
];

export const MANUAL_TASKS = [
  'Answering phones', 'Appointment confirmations', 'Insurance verification', 'Benefit breakdowns',
  'Hygiene recall calls', 'Cancellation backfill', 'Intake form chasing', 'Insurance card entry',
  'Claim creation', 'Claim scrubbing', 'Claim submission', 'Claim tracking',
  'EOB posting', 'Payment reconciliation', 'Underpayment review', 'Denial research',
  'Appeal writing', 'Patient statements', 'Balance follow-up', 'A/R aging work-down',
  'Treatment plan follow-up', 'Review requests', 'Fax sorting', 'Referral filing',
  'Payer hold time', 'Pre-auth submission', 'Schedule optimization', 'No-show follow-up',
  'Post-op check-ins', 'Reactivation outreach', 'Morning huddle prep', 'Clinical note entry',
  'Perio charting entry', 'Treatment plan writing', 'Patient education', 'Payment plan setup',
  'Records requests', 'Lab case tracking', 'Document filing', 'End-of-day reconciliation',
];

export const WHY_PILLARS = [
  {
    title: 'One platform, not ten subscriptions',
    body: 'Every tool below is the same product, the same login, the same data model. Nothing to integrate, nothing to sync, no vendor pointing at another vendor when something breaks.',
  },
  {
    title: 'Agents that act, not assistants that suggest',
    body: 'Most dental AI drafts a note or answers a phone, then hands the work back to your team. Asha\'s agents complete workflows end to end — verify, call, submit, post, appeal — and show their work.',
  },
  {
    title: 'A team with shared memory',
    body: 'Amanda\'s verification is what Ariel submits against, what Sarah appeals with, what Amy quotes on the phone. Point solutions can\'t do this — their context dies at the edge of each app.',
  },
  {
    title: 'Built in-house, on your data model',
    body: 'The AI is built by Asha, inside the platform, on dental workflows — not a white-labeled wrapper bolted onto someone else\'s PMS through an integration.',
  },
];

/* ============ SWITCHING ============ */

export const GO_LIVE_WEEKS = [
  {
    week: 'Week 1',
    title: 'Migration & setup',
    body: 'We migrate your data from your current PMS — or wire up a live integration if you\'re keeping it. Phones ordered, workflows configured, AI trained on your appointment types, providers, and scheduling rules.',
    items: ['Data migration or API integration', 'VoIP hardware ordered and provisioned', 'Scheduling rules and provider preferences configured', 'AI trained on your office workflows'],
  },
  {
    week: 'Week 2',
    title: 'Front office goes live',
    body: 'Amy starts answering calls and booking. Leena begins filling gaps. Stephanie starts recall outreach. Your team trains on the communication suite while the AI takes the busywork.',
    items: ['Amy live on calls and booking', 'Leena filling cancellations', 'Stephanie running hygiene recall', 'Staff training on the unified inbox'],
  },
  {
    week: 'Week 3',
    title: 'Calibrate & refine',
    body: 'We review real calls and real schedules with you, tune Amy\'s prompts and transfer logic, adjust scheduling rules to how your practice actually runs, and refine templates and automations.',
    items: ['Call review and voice tuning', 'Scheduling rules adjusted to reality', 'Templates, forms, and automations refined'],
  },
  {
    week: 'Week 4',
    title: 'Back office goes live',
    body: 'Amanda, Alayna, Ariel, Alisha, and Sarah switch on. Verification, claims, posting, and denials run end to end — with a human billing expert providing strategic oversight.',
    items: ['Insurance verification live', 'Claims submission and scrubbing live', 'EOB posting and denial management live', 'Full AI workforce operational'],
  },
];

export const MIGRATION_FACTS = [
  { title: 'Two ways to run it', body: 'Make Asha your PMS with a full migration — or keep your current PMS and run Asha\'s AI workforce on top through a live integration.' },
  { title: 'White-glove, not self-serve', body: 'A dedicated implementation team runs the cutover. Twice-weekly syncs through your first eight weeks. Your operations never pause.' },
  { title: 'Strategic advisors, not ticket support', body: 'Your account team has DSO operating experience. They review your numbers with you and suggest the next play — long after onboarding ends.' },
  { title: 'De-risked by design', body: 'Phased rollout: front office first, back office once you\'re comfortable. Parallel running during cutover, rollback procedures if anything surprises us.' },
];

/* ============ NAV / FOOTER ============ */

export const NAV = [
  {
    label: 'Platform',
    items: [
      { label: 'The Operating System', to: '/v2/platform', desc: '10+ software products, one login' },
      { label: 'Communication Suite', to: '/v2/communications', desc: 'Phones, texting, fax, forms, campaigns' },
      { label: 'Clinical AI', to: '/v2/clinical-ai', desc: 'Scribe, treatment planner, voice perio' },
      { label: 'ARIA — the engine', to: '/v2/aria', desc: 'The autonomy layer underneath it all' },
    ],
  },
  {
    label: 'AI Workforce',
    items: [
      { label: 'Meet the AI Team', to: '/v2/ai-team', desc: 'All 20+ agents, one shared brain' },
      { label: 'Front Office Agents', to: '/v2/front-office', desc: 'Calls, scheduling, recall, intake' },
      { label: 'Revenue Cycle Agents', to: '/v2/revenue-cycle', desc: 'Verification through appeals' },
    ],
  },
  {
    label: 'Why Asha',
    items: [
      { label: 'Why an operating system', to: '/v2/why-asha', desc: 'The case against ten point solutions' },
      { label: 'Switching to Asha', to: '/v2/switch', desc: 'White-glove go-live in 4 weeks' },
    ],
  },
];

export const FOOTER_COLS = [
  {
    title: 'Platform',
    links: [
      { label: 'The Operating System', to: '/v2/platform' },
      { label: 'Communication Suite', to: '/v2/communications' },
      { label: 'Clinical AI', to: '/v2/clinical-ai' },
      { label: 'ARIA Engine', to: '/v2/aria' },
    ],
  },
  {
    title: 'AI Workforce',
    links: [
      { label: 'Meet the AI Team', to: '/v2/ai-team' },
      { label: 'Front Office Agents', to: '/v2/front-office' },
      { label: 'Revenue Cycle Agents', to: '/v2/revenue-cycle' },
    ],
  },
  {
    title: 'Why Asha',
    links: [
      { label: 'Why an operating system', to: '/v2/why-asha' },
      { label: 'Switching to Asha', to: '/v2/switch' },
      { label: 'Book a demo', to: '/book-demo' },
      { label: 'Contact', to: '/contact' },
    ],
  },
  {
    title: 'Legal',
    links: [
      { label: 'Privacy Policy', to: '/privacy-policy-2026' },
      { label: 'Terms of Service', to: '/terms-of-service' },
      { label: 'BAA', to: '/baa' },
    ],
  },
];
